import React, { useEffect, useState } from "react";
import { CXM_EMBLEM_SHAPES, CXM_WORDMARK_SHAPES, CXM_LOGO_VIEWBOX } from "./cxmLogo";
import "./cxm-route-loader.scss";

interface CxmRouteLoaderProps {
  label?: string;
  /** Plays the exit: the finished logo lifts away and the frosted glass clears. */
  leaving?: boolean;
  /** Shown instead of `label` once the wait stops looking like a wait. */
  slowLabel?: string;
}

/**
 * When a wait stops reading as loading and starts reading as broken.
 *
 * The gateway's own client allows 30s for a cold connection, so a request that
 * is going to fail can hold this pane for half a minute. Half a minute of a
 * logo and nothing else is indistinguishable from a frozen widget — which is
 * exactly how it was reported. Saying so out loud costs nothing and is true.
 */
const SLOW_AFTER_MS = 5000;

/**
 * Seconds between one shape entering and the next.
 *
 * The shapes are already ordered left to right, so an even step gives both:
 * the reveal travels across the mark, and every shape still gets its own
 * moment. Spacing them by their actual x instead — which this did — collapsed
 * the emblem's four shards into 105ms, because they overlap horizontally and
 * sit in the leftmost eighth of the logo; the assembly disappeared and only
 * the wordmark looked staggered.
 *
 * Each shape animates for 0.36s, five times this step, so neighbours overlap
 * heavily and the seven entrances read as one wave rather than a stutter.
 * Changing it changes when the logo finishes, which `Withdrawal.tsx` waits for
 * — see `LOADER_ASSEMBLE_MS` there.
 */
const STEP_S = 0.07;

const delayFor = (rank: number): string => `${(rank * STEP_S).toFixed(3)}s`;

/**
 * Loading state for the CXM payout flow.
 *
 * A frosted pane over the page with the CXM logo at its centre. The mark
 * reveals itself shape by shape from the left — the emblem's four shards, then
 * the three letters, each drifting in as it appears — and a highlight
 * sweeps across it if the wait runs long. When the data lands the completed
 * logo lifts away and the glass clears onto the form.
 *
 * The paths are the brand asset itself (see `cxmLogo.ts`), not a redraw.
 */
const CxmRouteLoader: React.FC<CxmRouteLoaderProps> = ({ label, leaving = false, slowLabel }): JSX.Element => {
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    if (leaving) return undefined;
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  const text = slow && slowLabel && !leaving ? slowLabel : label;

  return (
    <div className={`cxm-route-loader${leaving ? " cxm-route-loader--leaving" : ""}`} role="status" aria-live="polite">
      <div className="cxm-route-loader__mark">
        <svg className="cxm-route-loader__svg" viewBox={CXM_LOGO_VIEWBOX} width="166" aria-hidden="true">
          <defs>
            <linearGradient id="cxm-shine" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stopColor="#fff" stopOpacity="0" />
              <stop offset="0.5" stopColor="#fff" stopOpacity="0.85" />
              <stop offset="1" stopColor="#fff" stopOpacity="0" />
            </linearGradient>
            <mask id="cxm-logo-mask">
              {[...CXM_EMBLEM_SHAPES, ...CXM_WORDMARK_SHAPES].map((s) => (
                <path key={s.d.slice(0, 24)} d={s.d} fill="#fff" />
              ))}
            </mask>
          </defs>

          <g className="cxm-route-loader__emblem">
            {CXM_EMBLEM_SHAPES.map((s, i) => (
              <path key={s.d.slice(0, 24)} d={s.d} className="cxm-route-loader__shape" style={{ animationDelay: delayFor(i) }} />
            ))}
          </g>

          <g className="cxm-route-loader__wordmark">
            {CXM_WORDMARK_SHAPES.map((s, i) => (
              <path
                key={s.d.slice(0, 24)}
                d={s.d}
                className="cxm-route-loader__shape"
                style={{ animationDelay: delayFor(CXM_EMBLEM_SHAPES.length + i) }}
              />
            ))}
          </g>

          <g mask="url(#cxm-logo-mask)" className="cxm-route-loader__shine">
            <rect x="-900" y="-100" width="700" height="1400" fill="url(#cxm-shine)" transform="skewX(-18)" />
          </g>
        </svg>
      </div>

      {text ? <p className="cxm-route-loader__label">{text}</p> : null}
    </div>
  );
};

export default CxmRouteLoader;
